/**
 * WebSocket Services Initialization
 * Manages lifecycle of all WebSocket singleton services.
 *
 * Now uses a single unified RealtimeService instead of 4 separate services.
 */

import realtimeService from './realtimeService';

// Legacy imports — kept for backward compatibility during migration.
// Components that still import these directly will continue to work.
import notificationService from './notificationService';
import taskService from './taskService';
import ticketService from './ticketService';
import broadcastService from './broadcastService';

/**
 * Initialize all WebSocket services on user login.
 * @param {object} user - User object with id and assigned_portal
 */
export const initializeWebSockets = (user) => {
  if (!user || !user.id) {
    console.log('[WebSocketServices] No user provided, skipping initialization');
    return;
  }

  console.log(
    `[WebSocketServices] Initializing unified WebSocket for user ${user.id}, portal: ${user.assigned_portal}`
  );

  // Primary: unified realtime service (1 connection for everything)
  realtimeService.initialize(user.id, user.assigned_portal);

  console.log('[WebSocketServices] Unified WebSocket initialized');
};

/**
 * Disconnect all WebSocket services on user logout.
 */
export const disconnectWebSockets = () => {
  console.log('[WebSocketServices] Disconnecting');
  realtimeService.disconnect();
  // Also disconnect legacy services in case they're still connected
  notificationService.disconnect();
  taskService.disconnect();
  ticketService.disconnect();
  broadcastService.disconnect();
  console.log('[WebSocketServices] All disconnected');
};

/**
 * Get connection status for all services.
 */
export const getConnectionStatuses = () => {
  return {
    realtime: realtimeService.getConnectionStatus(),
    // Legacy
    notifications: notificationService.getConnectionStatus(),
    tasks: taskService.getConnectionStatus(),
    tickets: ticketService.getConnectionStatus(),
    broadcasts: broadcastService.getConnectionStatus(),
  };
};

/**
 * Reconnect (useful for debugging or manual refresh).
 */
export const reconnectAll = (user) => {
  disconnectWebSockets();
  setTimeout(() => initializeWebSockets(user), 1000);
};

// Export services for direct access if needed
export { realtimeService, notificationService, taskService, ticketService, broadcastService };
